import axios from 'axios'
import clsx from 'clsx'
import {CSSProperties, FormEvent, useEffect, useMemo, useRef, useState} from 'react'
import {brandCopyright, KTIcon, WebSite} from '../../../../_metronic/helpers'
import {hasMegaPayBusinessCapabilities} from '../../../../_metronic/helpers/brand'
import {AppBackdrop} from '../../../theme/AppBackdrop'
import {BrandLockup, brandLockupName} from '../../../theme/BrandLogo'
import {
  categoryLabelPath as getCategoryPath,
  choiceOfLabel,
  countForFilters,
  formatStuffIdNumber as formatNumber,
  groupIcon,
  isSearchableStuffIdTerm,
  loadStuffIdGroups,
  normalizeStuffIdSearch as normalizeSearchInput,
  parseStuffIdTotal as parseTotal,
  StuffIdCategoryLabel,
  StuffIdFilters,
  StuffIdGroup,
  stuffIdSearchErrorMessage as getSearchErrorMessage,
  STUFFID_SEARCH_URL as SEARCH_URL,
} from './stuffidCatalog'
import seo from './stuffidSeo.json'
import {
  DEFAULT_STUFFID_FILTERS,
  isStuffIdHostname,
  readStuffIdCategory,
  readStuffIdFilters,
  readStuffIdQuery,
  withStuffIdSearchParams,
} from './stuffidSite'
import {ASA_REGISTRATION_URL} from '../../auth/core/membership'
import {PaymentResultBanner, readPaymentParam} from '../../online-payment/components/PaymentResultBanner'
import {StuffIdAccountBar} from './StuffIdAccountBar'
import {StuffIdCategoryLock} from './StuffIdCategoryLock'
import {CategoryLockState, readCategoryLock} from './stuffidAccessApi'
import './StuffIdSearchPage.css'

type StuffIdResult = {
  ix?: number
  cd?: string
  ds?: string
  dt?: string
  rt?: number | string
  tp?: string
  ck?: string
  cn?: string
  cc?: number
  tg?: StuffIdCategoryLabel[]
}

type FilterOption<K extends keyof StuffIdFilters> = [StuffIdFilters[K], string]

// The three filters above the results, each a segment as «همه / کالا / خدمت» was.
const FILTER_SEGMENTS: {
  [K in keyof StuffIdFilters]: {label: string; options: Array<FilterOption<K>>}
} = {
  kind: {
    label: 'کالا یا خدمت',
    options: [
      ['', 'همه'],
      ['1', 'کالا'],
      ['2', 'خدمت'],
    ],
  },
  scope: {
    label: 'شناسه عمومی یا اختصاصی',
    options: [
      ['', 'همه'],
      ['1', 'عمومی'],
      ['2', 'اختصاصی'],
    ],
  },
  vat: {
    label: 'مالیات بر ارزش افزوده',
    options: [
      ['', 'همه'],
      ['1', 'مشمول'],
      ['2', 'معاف'],
    ],
  },
}

// ASA's own panel and site; other brands show the search alone.
const isAsaBrand = hasMegaPayBusinessCapabilities()
const ASA_PANEL_URL = 'https://my.asatsp.ir'
// The same title as the page's own HTML on stuffid.asatsp.ir (scripts/stuffid-page.js).
const PAGE_TITLE = isAsaBrand ? seo.title : `${seo.heading} | ${brandLockupName}`

// Staggered entrance order for the site's «hero-in» motion.
const enter = (index: number) => ({'--i': index} as CSSProperties)

// An empty term browses the whole catalog or the chosen group.
const canRunSearch = (value: string) => !value.trim() || isSearchableStuffIdTerm(value)

// A full 13-digit code names one ID: it is looked up whatever the filters say.
const isExactCode = (value: string) => /^\d{13}$/.test(value.trim())

const getPageNumbers = (page: number, lastPage: number) => {
  if (lastPage <= 5) {
    return Array.from({length: lastPage}, (_, index) => index + 1)
  }

  const pages = new Set<number>([1, lastPage, page - 1, page, page + 1])
  return Array.from(pages)
    .filter((item) => item >= 1 && item <= lastPage)
    .sort((first, second) => first - second)
}

const kindOf = (item: StuffIdResult): '1' | '2' => (item.tp?.includes('خدمت') ? '2' : '1')

const scopeOf = (item: StuffIdResult): '1' | '2' => (item.tp?.includes('عمومی') ? '1' : '2')

const getVatRate = (item: StuffIdResult) => {
  const rate = Number(item.rt || 0)
  return Number.isFinite(rate) ? rate : 0
}

// A link such as https://stuffid.asatsp.ir/?q=کابل opens with its term already searched,
// ?g=<group>&t=<subgroup> with that group chosen and ?kind=&scope=&vat= with those filters; the
// address follows the search as it changes.
const getInitialQuery = () => normalizeSearchInput(readStuffIdQuery(window.location.search))
const getInitialCategory = () => readStuffIdCategory(window.location.search)

const StuffIdSearchPage = () => {
  const [query, setQuery] = useState(getInitialQuery)
  const [effectiveQuery, setEffectiveQuery] = useState(() => query.trim())
  const [filters, setFilters] = useState<StuffIdFilters>(() =>
    readStuffIdFilters(window.location.search)
  )
  const [manualCategoryId, setManualCategoryId] = useState(() =>
    String(getInitialCategory().groupId || '')
  )
  const [manualTagId, setManualTagId] = useState(() => String(getInitialCategory().tagId || ''))
  const [categoryBrowserOpen, setCategoryBrowserOpen] = useState(false)
  const [categorySearch, setCategorySearch] = useState('')
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<number[]>(() => {
    const groupId = getInitialCategory().groupId
    return groupId ? [groupId] : []
  })

  const [manualCategories, setManualCategories] = useState<StuffIdGroup[]>([])
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [items, setItems] = useState<StuffIdResult[]>([])
  const [total, setTotal] = useState(0)
  const [totalIsExact, setTotalIsExact] = useState(true)
  const [catalogTotal, setCatalogTotal] = useState<number>()
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [requestVersion, setRequestVersion] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  // A search by category the asker may not browse (2026-10-18): the lock shows where the results would.
  const [locked, setLocked] = useState<{state: CategoryLockState; message: string}>()
  // Back from the gateway after buying the categories: the payment's result.
  const [paymentId, setPaymentId] = useState(readPaymentParam)
  const [copiedCode, setCopiedCode] = useState('')
  const resultsRef = useRef<HTMLDivElement>(null)
  const requestIdRef = useRef(0)

  const codeSearch = isExactCode(effectiveQuery)

  // A link may name only the subgroup (?t=); its group is found in the tree.
  const selectedManualCategory = useMemo(
    () =>
      manualCategories.find((category) => String(category.id) === manualCategoryId) ||
      (manualTagId
        ? manualCategories.find((category) =>
            category.tags.some((tag) => String(tag.id) === manualTagId)
          )
        : undefined),
    [manualCategories, manualCategoryId, manualTagId]
  )
  const selectedManualTag = useMemo(
    () => selectedManualCategory?.tags.find((tag) => String(tag.id) === manualTagId),
    [manualTagId, selectedManualCategory]
  )
  const selectedCategoryName = selectedManualTag
    ? `${selectedManualCategory?.name} / ${selectedManualTag.name}`
    : selectedManualCategory?.name || ''

  // Groups and subgroups with what the filters leave of them; empty ones are left out, except the
  // one chosen.
  const visibleCategories = useMemo(() => {
    const term = normalizeSearchInput(categorySearch).trim()
    return manualCategories
      .map((category) => {
        const tags = category.tags
          .map((tag) => ({...tag, shown: countForFilters(tag, filters)}))
          .filter((tag) => tag.shown > 0 || String(tag.id) === manualTagId)
        const rootMatches = !term || category.name.includes(term)
        return {
          ...category,
          shown: countForFilters(category, filters),
          tags: rootMatches ? tags : tags.filter((tag) => tag.name.includes(term)),
          rootMatches,
        }
      })
      .filter(
        (category) =>
          (category.shown > 0 ||
            String(category.id) === manualCategoryId ||
            category.tags.some((tag) => String(tag.id) === manualTagId)) &&
          (category.rootMatches || category.tags.length > 0)
      )
  }, [categorySearch, filters, manualCategories, manualCategoryId, manualTagId])

  const hasAnyFilters = Boolean(
    filters.kind !== DEFAULT_STUFFID_FILTERS.kind ||
      filters.scope !== DEFAULT_STUFFID_FILTERS.scope ||
      filters.vat !== DEFAULT_STUFFID_FILTERS.vat ||
      manualCategoryId ||
      manualTagId
  )

  useEffect(() => {
    const previousTitle = document.title
    return () => {
      document.title = previousTitle
    }
  }, [])

  // The browser tab and history say what is shown: «term» or the group, then the page's title.
  useEffect(() => {
    const subject = effectiveQuery
      ? `«${effectiveQuery}»`
      : selectedCategoryName
      ? `شناسه‌های ${selectedCategoryName}`
      : ''
    document.title = subject ? `${subject} | ${PAGE_TITLE}` : PAGE_TITLE
  }, [effectiveQuery, selectedCategoryName])

  useEffect(() => {
    const timeout = window.setTimeout(
      () => {
        setEffectiveQuery(query.trim())
        setPage(1)
      },
      isExactCode(query) ? 60 : 280
    )
    return () => window.clearTimeout(timeout)
  }, [query])

  useEffect(() => {
    let alive = true
    setLoadingCategories(true)
    loadStuffIdGroups()
      .then((groups) => alive && setManualCategories(groups))
      .catch(() => alive && setManualCategories([]))
      .finally(() => alive && setLoadingCategories(false))
    return () => {
      alive = false
    }
  }, [])

  // The size of the whole catalog for the line under the search, whatever the filters are.
  useEffect(() => {
    let alive = true
    axios
      .get(SEARCH_URL, {params: {page: 1, items_per_page: 1, search: ''}, timeout: 30000})
      .then((response) => {
        const pagination = response.data?.payload?.pagination
        if (alive && pagination?.totalIsExact !== false) {
          setCatalogTotal(parseTotal(pagination?.filterTotal))
        }
      })
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    const search = withStuffIdSearchParams(
      window.location.search,
      effectiveQuery,
      {groupId: Number(manualCategoryId) || undefined, tagId: Number(manualTagId) || undefined},
      filters
    )
    if (search !== window.location.search) {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${search}${window.location.hash}`
      )
    }
  }, [effectiveQuery, filters, manualCategoryId, manualTagId])

  useEffect(() => {
    const requestId = ++requestIdRef.current
    if (!canRunSearch(effectiveQuery)) {
      setItems([])
      setTotal(0)
      setTotalIsExact(true)
      setHasMore(false)
      setHasSearched(false)
      setLoading(false)
      setError('')
      return
    }

    const source = axios.CancelToken.source()
    setLoading(true)
    setHasSearched(true)
    setError('')
    const lookup = isExactCode(effectiveQuery)

    axios
      .get(SEARCH_URL, {
        cancelToken: source.token,
        timeout: 60000,
        params: {
          page,
          items_per_page: pageSize,
          search: effectiveQuery,
          filter_god: (!lookup && filters.scope) || undefined,
          filter_gos: (!lookup && filters.kind) || undefined,
          filter_mom: (!lookup && filters.vat) || undefined,
          filter_t: (!lookup && manualTagId) || undefined,
          filter_tg: (!lookup && !manualTagId && manualCategoryId) || undefined,
        },
      })
      .then((response) => {
        if (requestIdRef.current !== requestId) {
          return
        }
        setLocked(undefined)
        setItems(response.data?.data || [])
        const pagination = response.data?.payload?.pagination
        setTotal(parseTotal(pagination?.filterTotal))
        setTotalIsExact(pagination?.totalIsExact !== false)
        setHasMore(pagination?.hasMore === true)
      })
      .catch((requestError) => {
        if (!axios.isCancel(requestError) && requestIdRef.current === requestId) {
          setItems([])
          setTotal(0)
          setTotalIsExact(true)
          setHasMore(false)
          const lock = readCategoryLock(requestError)
          setLocked(lock)
          setError(lock ? '' : getSearchErrorMessage(requestError))
        }
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setLoading(false)
        }
      })

    return () => source.cancel('search changed')
  }, [effectiveQuery, filters, manualCategoryId, manualTagId, page, pageSize, requestVersion])

  useEffect(() => {
    if (!copiedCode) {
      return
    }
    const timeout = window.setTimeout(() => setCopiedCode(''), 1600)
    return () => window.clearTimeout(timeout)
  }, [copiedCode])

  const runSearch = (event?: FormEvent) => {
    event?.preventDefault()
    setEffectiveQuery(query.trim())
    setPage(1)
    setRequestVersion((current) => current + 1)
  }

  const setFilter = <K extends keyof StuffIdFilters>(key: K, value: StuffIdFilters[K]) => {
    setFilters((current) => (current[key] === value ? current : {...current, [key]: value}))
    setPage(1)
  }

  const clearEverything = () => {
    setQuery('')
    setEffectiveQuery('')
    setFilters(DEFAULT_STUFFID_FILTERS)
    setManualCategoryId('')
    setManualTagId('')
    setPage(1)
  }

  const selectManualCategory = (categoryId: number) => {
    setManualCategoryId(String(categoryId))
    setManualTagId('')
    setExpandedCategoryIds((current) =>
      current.includes(categoryId) ? current : [...current, categoryId]
    )
    setPage(1)
    setCategoryBrowserOpen(false)
  }

  const selectManualTag = (categoryId: number, tagId: number) => {
    setManualCategoryId(String(categoryId))
    setManualTagId(String(tagId))
    setExpandedCategoryIds((current) =>
      current.includes(categoryId) ? current : [...current, categoryId]
    )
    setPage(1)
    setCategoryBrowserOpen(false)
  }

  const clearManualCategory = () => {
    setManualCategoryId('')
    setManualTagId('')
    setPage(1)
  }

  const toggleCategoryExpansion = (categoryId: number) => {
    setExpandedCategoryIds((current) =>
      current.includes(categoryId)
        ? current.filter((id) => id !== categoryId)
        : [...current, categoryId]
    )
  }

  const copyCode = async (code?: string) => {
    if (!code) {
      return
    }
    try {
      await navigator.clipboard.writeText(code)
    } catch {
      const input = document.createElement('textarea')
      input.value = code
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      input.remove()
    }
    setCopiedCode(code)
  }

  const scrollToResults = () =>
    window.setTimeout(
      () => resultsRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'}),
      0
    )

  const changePage = (nextPage: number) => {
    setPage(nextPage)
    scrollToResults()
  }

  // A result's chips set the filter they show: its kind, its scope, its tax, its group.
  const filterBy = <K extends keyof StuffIdFilters>(key: K, value: StuffIdFilters[K]) => {
    setFilter(key, value)
    scrollToResults()
  }

  const selectResultCategory = (category: StuffIdCategoryLabel) => {
    const choice = choiceOfLabel(category)
    if (choice.tagId && choice.groupId) {
      selectManualTag(choice.groupId, choice.tagId)
    } else if (choice.groupId) {
      selectManualCategory(choice.groupId)
    }
    scrollToResults()
  }

  const lastPage = Math.max(1, Math.ceil(total / pageSize))
  const pageNumbers = getPageNumbers(page, lastPage)
  const selectedGroupName = selectedManualCategory?.name || ''

  const filterSegment = <K extends keyof StuffIdFilters>(key: K) => {
    const segment = FILTER_SEGMENTS[key]
    return (
      <div className='asa-stuffid__segment' role='group' aria-label={segment.label}>
        {segment.options.map(([value, label]) => (
          <button
            key={value || 'all'}
            type='button'
            className={clsx(filters[key] === value && 'is-active')}
            aria-pressed={filters[key] === value}
            onClick={() => setFilter(key, value)}
          >
            {label}
          </button>
        ))}
      </div>
    )
  }

  const categoryBrowser = (
    <>
      <div className='asa-stuffid-groups__head'>
        <div className='d-flex align-items-center gap-3 min-w-0'>
          <span className='asa-stuffid-groups__icon' aria-hidden='true'>
            <KTIcon iconName='category' className='fs-2' />
          </span>
          <div className='min-w-0'>
            <h2 className='asa-stuffid-groups__title'>گروه‌های شناسه</h2>
            <span className='asa-stuffid-groups__count'>
              {formatNumber(visibleCategories.length)} گروه
            </span>
          </div>
        </div>
        {(manualCategoryId || manualTagId) && (
          <button
            type='button'
            className='btn btn-sm btn-icon btn-light'
            onClick={clearManualCategory}
            title='نمایش همه گروه‌ها'
            aria-label='نمایش همه گروه‌ها'
          >
            <KTIcon iconName='cross' className='fs-3' />
          </button>
        )}
      </div>

      <div className='asa-stuffid-groups__search'>
        <KTIcon iconName='magnifier' className='fs-3' />
        <input
          type='search'
          className='form-control form-control-sm'
          placeholder='جستجو در گروه‌ها'
          aria-label='جستجو در گروه‌ها'
          value={categorySearch}
          onChange={(event) => setCategorySearch(event.target.value)}
        />
      </div>

      <div className='asa-stuffid-groups__list'>
        <button
          type='button'
          className={clsx(
            'asa-stuffid-groups__item',
            !manualCategoryId && !manualTagId && 'is-active'
          )}
          aria-pressed={!manualCategoryId && !manualTagId}
          onClick={clearManualCategory}
        >
          <i className='las la-layer-group asa-stuffid-groups__glyph' aria-hidden='true' />
          <span className='flex-grow-1'>همه گروه‌ها</span>
        </button>

        {loadingCategories ? (
          Array.from({length: 7}, (_, index) => (
            <div key={index} className='asa-stuffid-groups__placeholder'>
              <span className='placeholder col-12 h-15px' />
            </div>
          ))
        ) : visibleCategories.length === 0 ? (
          <div className='asa-stuffid-groups__empty'>گروهی پیدا نشد.</div>
        ) : (
          visibleCategories.map((category) => {
            const groupSelected = String(category.id) === manualCategoryId && !manualTagId
            const expanded =
              Boolean(categorySearch.trim()) || expandedCategoryIds.includes(category.id)
            return (
              <div key={category.id} className='asa-stuffid-groups__group'>
                <div className='d-flex align-items-stretch'>
                  <button
                    type='button'
                    className={clsx(
                      'asa-stuffid-groups__item flex-grow-1 min-w-0',
                      groupSelected && 'is-active'
                    )}
                    aria-pressed={groupSelected}
                    onClick={() => selectManualCategory(category.id)}
                  >
                    <i
                      className={`las ${groupIcon(category.name)} asa-stuffid-groups__glyph`}
                      aria-hidden='true'
                    />
                    <span className='text-break flex-grow-1'>{category.name}</span>
                    <span
                      className='asa-stuffid-groups__badge'
                      title={`${formatNumber(category.shown)} شناسه`}
                    >
                      {formatNumber(category.shown)}
                    </span>
                  </button>
                  {category.tags.length > 0 && (
                    <button
                      type='button'
                      className='asa-stuffid-groups__toggle'
                      onClick={() => toggleCategoryExpansion(category.id)}
                      title={expanded ? 'بستن زیرگروه‌ها' : 'نمایش زیرگروه‌ها'}
                      aria-label={expanded ? 'بستن زیرگروه‌ها' : 'نمایش زیرگروه‌ها'}
                      aria-expanded={expanded}
                    >
                      <i className={`bi bi-chevron-${expanded ? 'up' : 'down'} fs-6`} />
                    </button>
                  )}
                </div>

                {expanded && category.tags.length > 0 && (
                  <div className='asa-stuffid-groups__tags'>
                    {category.tags.map((tag) => {
                      const tagSelected = String(tag.id) === manualTagId
                      return (
                        <button
                          key={tag.id}
                          type='button'
                          className={clsx('asa-stuffid-groups__tag', tagSelected && 'is-active')}
                          aria-pressed={tagSelected}
                          onClick={() => selectManualTag(category.id, tag.id)}
                        >
                          <span className='asa-stuffid-groups__dot' aria-hidden='true' />
                          <span className='text-break flex-grow-1'>{tag.name}</span>
                          <span
                            className='asa-stuffid-groups__tag-count'
                            title={`${formatNumber(tag.shown)} شناسه`}
                          >
                            {formatNumber(tag.shown)}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </>
  )

  return (
    <div id='stuffid-search-page' className='asa-stuffid'>
      <AppBackdrop site />

      <header className='asa-stuffid__bar'>
        <BrandLockup href={WebSite || '/'} />
        <StuffIdAccountBar
          panelUrl={ASA_PANEL_URL}
          registrationUrl={ASA_REGISTRATION_URL}
          showGuestLinks={isAsaBrand}
        />
      </header>

      <section className='asa-stuffid__hero' aria-labelledby='stuffid-search-title'>
        <p className='asa-stuffid__badge asa-stuffid-enter' style={enter(0)}>
          <i className='las la-gift' aria-hidden='true' /> رایگان و بدون نیاز به ثبت‌نام
        </p>
        <h1
          id='stuffid-search-title'
          className='asa-stuffid__title asa-stuffid-enter'
          style={enter(1)}
        >
          جستجوی شناسه <span className='asa-text-gradient'>کالا و خدمت</span>
        </h1>
        <p className='asa-stuffid__lead asa-stuffid-enter' style={enter(2)}>
          شناسه ۱۳ رقمی هر کالا یا خدمت را در فهرست شناسه‌های عمومی و اختصاصی سامانه مؤدیان پیدا
          کنید؛ نوع، نرخ مالیات بر ارزش افزوده و گروه آن را ببینید و با یک کلیک کپی کنید.
        </p>

        <div className='asa-stuffid__panel asa-stuffid-enter' style={enter(3)}>
          <form onSubmit={runSearch} role='search' aria-label='جستجوی شناسه کالا/خدمت'>
            <div className='asa-stuffid__field'>
              <KTIcon iconName='magnifier' className='asa-stuffid__field-icon fs-1' />
              <input
                type='text'
                inputMode='search'
                autoComplete='off'
                autoFocus
                className='asa-stuffid__input'
                placeholder='شناسه ۱۳ رقمی یا بخشی از نام کالا/خدمت'
                aria-label='شناسه یا شرح کالا/خدمت'
                value={query}
                onChange={(event) => setQuery(normalizeSearchInput(event.target.value))}
                dir='auto'
              />
              {query && (
                <button
                  type='button'
                  className='asa-stuffid__clear'
                  onClick={() => setQuery('')}
                  title='پاک کردن عبارت'
                  aria-label='پاک کردن عبارت'
                >
                  <KTIcon iconName='cross' className='fs-2' />
                </button>
              )}
              <button
                type='submit'
                className='btn btn-primary asa-stuffid__submit'
                disabled={loading}
              >
                {loading ? <span className='spinner-border spinner-border-sm' /> : 'جستجو'}
              </button>
            </div>

            <div className='asa-stuffid__controls'>
              {filterSegment('kind')}
              {filterSegment('scope')}
              {filterSegment('vat')}

              <button
                type='button'
                className={clsx(
                  'asa-stuffid__chip d-xl-none',
                  (manualCategoryId || manualTagId) && 'is-active'
                )}
                onClick={() => setCategoryBrowserOpen((current) => !current)}
                aria-expanded={categoryBrowserOpen}
              >
                <i
                  className={`las ${
                    selectedGroupName ? groupIcon(selectedGroupName) : 'la-layer-group'
                  } fs-3`}
                  aria-hidden='true'
                />
                <span className='text-truncate'>{selectedCategoryName || 'گروه‌ها'}</span>
              </button>
            </div>
          </form>

          {selectedCategoryName && (
            <div className='asa-stuffid__active'>
              <button
                type='button'
                className='asa-stuffid__active-chip'
                onClick={clearManualCategory}
                aria-label={`حذف فیلتر ${selectedCategoryName}`}
              >
                <i className={`las ${groupIcon(selectedGroupName)}`} aria-hidden='true' />
                {selectedCategoryName}
                <KTIcon iconName='cross' className='fs-5' />
              </button>
            </div>
          )}

          {categoryBrowserOpen && (
            <div className='asa-stuffid-groups asa-stuffid-groups--inline d-xl-none'>
              {categoryBrowser}
            </div>
          )}
        </div>

        <ul className='asa-stuffid__facts asa-stuffid-enter' style={enter(4)}>
          {catalogTotal !== undefined && catalogTotal > 0 && (
            <li>
              <i className='las la-check' aria-hidden='true' />
              {formatNumber(catalogTotal)} شناسه کالا و خدمت
            </li>
          )}
          {manualCategories.length > 0 && (
            <li>
              <i className='las la-check' aria-hidden='true' />
              {formatNumber(manualCategories.length)} گروه دسته‌بندی
            </li>
          )}
          <li>
            <i className='las la-check' aria-hidden='true' />
            کپی شناسه با یک کلیک
          </li>
        </ul>
      </section>

      <main className='asa-stuffid__main'>
        <aside className='asa-stuffid__aside d-none d-xl-block' aria-label='گروه‌های شناسه'>
          <div className='asa-stuffid-groups'>{categoryBrowser}</div>
        </aside>

        <section className='asa-stuffid__results' aria-labelledby='stuffid-results-title'>
          <div ref={resultsRef} className='asa-stuffid__results-head'>
            <div className='min-w-0'>
              <div className='d-flex flex-wrap align-items-center gap-2 mb-1'>
                <h2 id='stuffid-results-title' className='asa-stuffid__results-title'>
                  نتایج
                </h2>
                {selectedCategoryName && !codeSearch && (
                  <span className='badge badge-light-primary text-truncate mw-300px'>
                    {selectedCategoryName}
                  </span>
                )}
              </div>
              <span className='asa-stuffid__results-count' aria-live='polite'>
                {hasSearched
                  ? totalIsExact
                    ? `${formatNumber(total)} شناسه`
                    : `بیش از ${formatNumber(Math.max(0, total - 1))} شناسه`
                  : 'آماده جستجو'}
                {codeSearch && ' — جستجوی شناسه ۱۳ رقمی در همه شناسه‌ها، بدون فیلتر'}
              </span>
            </div>
            <div className='d-flex align-items-center gap-2'>
              {(query || hasAnyFilters) && (
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  onClick={clearEverything}
                  title='پاک کردن جستجو و فیلترها'
                >
                  <KTIcon iconName='cross' className='fs-4' />
                  پاک کردن
                </button>
              )}
              <select
                className='form-select form-select-sm w-90px'
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value))
                  setPage(1)
                }}
                aria-label='تعداد نتیجه در صفحه'
              >
                <option value={25}>۲۵</option>
                <option value={50}>۵۰</option>
                <option value={100}>۱۰۰</option>
              </select>
            </div>
          </div>

          {error && <div className='alert alert-danger rounded-0 border-0 m-0'>{error}</div>}
          {paymentId && (
            <div className='p-4'>
              <PaymentResultBanner
                publicId={paymentId}
                invoiceBase={isStuffIdHostname(window.location.hostname) ? ASA_PANEL_URL : ''}
                onSettled={() => setRequestVersion((current) => current + 1)}
                onClose={() => setPaymentId(undefined)}
              />
            </div>
          )}

          {locked && !loading ? (
            <StuffIdCategoryLock
              state={locked.state}
              message={locked.message}
              onOpened={() => {
                setLocked(undefined)
                setRequestVersion((current) => current + 1)
              }}
              onClearCategory={() => {
                setLocked(undefined)
                setManualCategoryId('')
                setManualTagId('')
              }}
            />
          ) : loading ? (
            <ol className='asa-stuffid-list' aria-busy='true'>
              {Array.from({length: 6}, (_, index) => (
                <li key={index} className='asa-stuffid-item'>
                  <span className='placeholder col-12 h-20px' />
                </li>
              ))}
            </ol>
          ) : items.length > 0 ? (
            <ol className='asa-stuffid-list'>
              {items.map((item, index) => {
                const kind = kindOf(item)
                const scope = scopeOf(item)
                const vatRate = getVatRate(item)
                const copied = copiedCode === item.cd
                return (
                  <li key={`${item.cd}:${item.ix ?? index}`} className='asa-stuffid-item'>
                    <div className='asa-stuffid-item__id'>
                      <span className='asa-stuffid__code' dir='ltr'>
                        {item.cd}
                      </span>
                      {item.dt && (
                        <span className='asa-stuffid-item__date'>
                          آخرین ویرایش <span dir='ltr'>{item.dt}</span>
                        </span>
                      )}
                    </div>

                    <div className='asa-stuffid-item__body'>
                      <p className='asa-stuffid-item__title'>{item.ds}</p>
                      <div className='asa-stuffid-item__tags'>
                        <button
                          type='button'
                          className={clsx(
                            'asa-stuffid-tag',
                            kind === '2' ? 'is-service' : 'is-goods'
                          )}
                          onClick={() => filterBy('kind', kind)}
                          title={kind === '2' ? 'فقط خدمت‌ها' : 'فقط کالاها'}
                        >
                          {kind === '2' ? 'خدمت' : 'کالا'}
                        </button>
                        <button
                          type='button'
                          className={clsx(
                            'asa-stuffid-tag',
                            scope === '1' ? 'is-general' : 'is-dedicated'
                          )}
                          onClick={() => filterBy('scope', scope)}
                          title={scope === '1' ? 'فقط شناسه‌های عمومی' : 'فقط شناسه‌های اختصاصی'}
                        >
                          {scope === '1' ? 'عمومی' : 'اختصاصی'}
                        </button>
                        <button
                          type='button'
                          className={clsx(
                            'asa-stuffid-tag',
                            vatRate > 0 ? 'is-taxable' : 'is-exempt'
                          )}
                          onClick={() => filterBy('vat', vatRate > 0 ? '1' : '2')}
                          title={vatRate > 0 ? 'فقط شناسه‌های مشمول مالیات' : 'فقط شناسه‌های معاف'}
                        >
                          {vatRate > 0 ? `مالیات ${formatNumber(vatRate)}٪` : 'معاف از مالیات'}
                        </button>
                        {item.tg?.length ? (
                          item.tg.slice(0, 2).map((category) => (
                            <button
                              key={category.id}
                              type='button'
                              className='asa-stuffid-tag is-group'
                              onClick={() => selectResultCategory(category)}
                              title='نمایش شناسه‌های این گروه'
                            >
                              <i
                                className={`las ${groupIcon(category.gn || category.nm)}`}
                                aria-hidden='true'
                              />
                              {getCategoryPath(category)}
                            </button>
                          ))
                        ) : (
                          <span className='asa-stuffid-tag is-muted'>بدون گروه</span>
                        )}
                      </div>
                    </div>

                    <button
                      type='button'
                      className={clsx(
                        'btn btn-sm btn-icon asa-stuffid-item__copy',
                        copied ? 'btn-light-success' : 'btn-light'
                      )}
                      onClick={() => copyCode(item.cd)}
                      title={copied ? 'کپی شد' : 'کپی شناسه'}
                      aria-label={copied ? 'کپی شد' : `کپی شناسه ${item.cd}`}
                    >
                      <KTIcon iconName={copied ? 'check' : 'copy'} className='fs-3' />
                    </button>
                  </li>
                )
              })}
            </ol>
          ) : (
            <div className='asa-stuffid__empty'>
              <KTIcon iconName='magnifier' className='fs-3x' />
              <span className='d-block'>
                {hasSearched ? 'نتیجه‌ای پیدا نشد.' : 'جستجوی شناسه کالا/خدمت'}
              </span>
              {hasSearched && hasAnyFilters && (
                <span className='d-block fs-7 mt-1'>
                  فیلترها یا گروه را تغییر دهید؛ «همه» همه شناسه‌ها را نشان می‌دهد.
                </span>
              )}
            </div>
          )}

          {hasSearched && total > 0 && (
            <div className='asa-stuffid__pager'>
              <span className='fs-7 text-gray-600'>
                {totalIsExact
                  ? `صفحه ${formatNumber(page)} از ${formatNumber(lastPage)}`
                  : `صفحه ${formatNumber(page)}؛ نتایج بیشتری موجود است`}
              </span>
              <div className='d-flex align-items-center gap-1'>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-light'
                  disabled={page <= 1 || loading}
                  onClick={() => changePage(page - 1)}
                  title='صفحه قبل'
                  aria-label='صفحه قبل'
                >
                  <KTIcon iconName='right' className='fs-3' />
                </button>
                {pageNumbers.map((pageNumber, index) => {
                  const previousPageNumber = pageNumbers[index - 1]
                  return (
                    <span key={pageNumber} className='d-flex align-items-center gap-1'>
                      {previousPageNumber && pageNumber - previousPageNumber > 1 && (
                        <span className='px-1 text-gray-500'>...</span>
                      )}
                      <button
                        type='button'
                        className={`btn btn-sm btn-icon ${
                          page === pageNumber ? 'btn-primary' : 'btn-light'
                        }`}
                        disabled={loading}
                        onClick={() => changePage(pageNumber)}
                        aria-current={page === pageNumber ? 'page' : undefined}
                      >
                        {formatNumber(pageNumber)}
                      </button>
                    </span>
                  )
                })}
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-light'
                  disabled={loading || (totalIsExact ? page >= lastPage : !hasMore)}
                  onClick={() => changePage(page + 1)}
                  title='صفحه بعد'
                  aria-label='صفحه بعد'
                >
                  <KTIcon iconName='left' className='fs-3' />
                </button>
              </div>
            </div>
          )}
        </section>
      </main>

      <section className='asa-stuffid__faq' aria-labelledby='stuffid-faq-title'>
        <h2 id='stuffid-faq-title'>پرسش‌های پرتکرار</h2>
        <div className='asa-stuffid__faq-list'>
          {seo.faq.map((item) => (
            <article key={item.question} className='asa-stuffid__faq-item'>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </div>
      </section>

      {isAsaBrand && (
        <section className='asa-stuffid__promo' aria-labelledby='stuffid-promo-title'>
          <div>
            <h2 id='stuffid-promo-title'>
              شناسه را پیدا کردید؟{' '}
              <span className='asa-text-gradient'>صورتحساب را با آسا صادر کنید.</span>
            </h2>
            <p>
              صدور و ارسال صورتحساب الکترونیکی به سامانه مؤدیان، از پنل وب، اکسل، API یا نرم‌افزار
              حسابداری شما.
            </p>
          </div>
          <div className='asa-stuffid__promo-actions'>
            <a href={ASA_REGISTRATION_URL} className='btn btn-primary'>
              ثبت‌نام رایگان
            </a>
            <a href={WebSite || '/'} className='btn btn-light'>
              آشنایی با آسا
            </a>
          </div>
        </section>
      )}

      <footer className='asa-stuffid__footer'>
        <p>
          <KTIcon iconName='information-5' className='fs-3' />
          <span>
            اطلاعات این صفحه از کاتالوگ سامانه مؤدیان تهیه می‌شود. انتخاب نهایی شناسه را با مرجع{' '}
            <a href='https://stuffid.tax.gov.ir/' target='_blank' rel='noreferrer'>
              شناسه کالا/خدمت سازمان امور مالیاتی
            </a>{' '}
            تطبیق دهید.
          </span>
        </p>
        <small>{brandCopyright}</small>
      </footer>
    </div>
  )
}

export {StuffIdSearchPage}
